import type { Messages } from '../catalogue.en'

/**
 * Access administration in the Control Center: user accounts, roles and
 * capabilities. Mirrors `en/controlCenterAccess.ts`.
 *
 * Composed into the public catalogue by `catalogue.he.ts`. Nothing
 * imports this module directly: consumers ask the catalogue, so how the
 * messages are stored stays this layer's business.
 */
export const controlCenterAccess: Partial<Messages> = {
  'workplaces.title': 'מקומות עבודה',
  'workplaces.remove': 'הסרת מקום עבודה',
  'workplaces.defaultBranch': 'סניף ברירת המחדל של הארגון החדש',
  'workplaces.classificationSettings': 'פרופיל סיווג מקומות עבודה',
  'workplaces.profile': 'פרופיל לאומי',
  'workplaces.changeProfile': 'שינוי פרופיל הסיווג',
  'workplaces.classificationUnsaved': 'פרופיל סיווג מקומות העבודה בארגון',
  'workplaces.empty': 'לא הוגדרו מקומות עבודה.',
  'workplaces.loadFailed': 'לא ניתן לטעון את מקומות העבודה.',
  'workplaces.add': 'הוספת מקום עבודה',
  'workplaces.edit': 'עריכת מקום עבודה',
  'workplaces.save': 'שמירת מקום עבודה',
  'workplaces.branch': 'סניף',
  'workplaces.profession': 'מקצוע',
  'workplaces.position': 'תפקיד',
  'workplaces.specialty': 'התמחות',
  'workplaces.active': 'מקום העבודה פעיל',
  'workplaces.available': 'זמין',
  'workplaces.unavailable': 'לא זמין',
  'workplaces.from': 'בתוקף מתאריך',
  'workplaces.until': 'בתוקף עד',
  'workplaces.il': 'משרד הבריאות בישראל',
  'workplaces.ru': 'משרד הבריאות ברוסיה',
  'workplaces.convert': 'החלת פרופיל הארגון ובחירת ערכים חדשים',
  'workplaces.unsaved': 'פרטי מקום העבודה',
  'workplaces.failed': 'מקומות העבודה אינם זמינים. הערכים שהוזנו נשמרו בטופס.',
  'workplaces.changed': 'מקום העבודה השתנה. יש לטעון את הגרסה השמורה לפני השמירה.',
  'workplaces.profileChanged': 'פרופיל הסיווג של הארגון השתנה. יש לרענן לפני השמירה.',
  'workplaces.duplicate': 'מקום עבודה זהה כבר תקף בתקופה זו.',
  'workplaces.futureBookings': 'יש להעביר או לבטל את התורים העתידיים המושפעים לפני שינוי מקום העבודה.',
  'workplaces.affectedBookings': 'תורים מושפעים',
  'workplaces.bookingCount': 'תורים מושפעים: {count}',
  'workplaces.bookingLink': 'פתיחת תור {number} בלשונית חדשה',
  'workplaces.codingInvalid': 'הקוד שנבחר אינו שייך לשדה זה במקום העבודה.',
  'workplaces.branchUnavailable': 'הסניף אינו זמין למקום עבודה פעיל.',
  'workplaces.invalid': 'יש לבדוק את השדות ואת תקופת התוקף של מקום העבודה.',
  'workplaces.forbidden': 'אין לך הרשאה לבצע שינוי זה במקום העבודה.',
  'workplaces.one': 'מקום עבודה',
  'workplaces.subtitle': 'באיזה סניף עובד כל קלינאי, ולפי איזו סיווג',
  'workplaces.employee': 'עובד',
  'workplaces.state': 'מצב',
  'workplaces.closed': 'הסתיים',
  'workplaces.create': 'מקום עבודה חדש',
  'workplaces.saved': 'מקום העבודה נשמר',
  'workplaces.loading': 'טוען מקומות עבודה',
  'workplaces.reloadFailed': 'לא ניתן לטעון את הגרסה השמורה.',
  'workplaces.readOnly': 'מקום עבודה זה פתוח לך לקריאה בלבד.',
  'workplaces.open': 'פתיחת מקומות עבודה',
  'workplaces.current': 'נוכחי',
  'workplaces.remembered': 'בפעם הקודמת',
  'workplaces.rememberFailed': 'לא ניתן היה לשמור את בחירת מקום העבודה. יש לנסות שוב.',
  'workplaces.enterWithout': 'כניסה ללא מקום עבודה',
  'workplaces.editClassification': 'שינוי הסיווג',
  'organizationSetup.title': 'הקמת ארגונים',
  'organizationSetup.create': 'יצירת ארגון',
  'organizationSetup.empty': 'אין בקשות להקמת ארגונים.',
  'organizationSetup.failed': 'הקמת הארגון אינה זמינה. הפרטים שהוזנו נשמרו.',
  'organizationSetup.changed': 'הבקשה השתנתה. יש לרענן את המצב השמור לפני ההמשך.',
  'organizationSetup.inputConflict': 'הבקשה כבר נשמרה עם פרטים אחרים. יש לסגור את הטופס ולפתוח את הבקשה השמורה.',
  'organizationSetup.closed': 'הפעולה אינה זמינה עוד לבקשה זו.',
  'organizationSetup.forbidden': 'אין לך הרשאה לנהל הקמת ארגונים.',
  'organizationSetup.ownerEmail': 'דוא״ל הבעלים',
  'organizationSetup.unsaved': 'פרטי ארגון חדש',
  'organizationSetup.unsavedLink': 'קישור הזמנת הבעלים שטרם הועתק',
  'organizationSetup.discard': 'למחוק את פרטי הארגון שהוזנו?',
  'organizationSetup.discardLink': 'קישור ההזמנה האישי לא יוצג עוד. לסגור בכל זאת?',
  'organizationSetup.cancel': 'ביטול ההקמה',
  'organizationSetup.cancelConfirm': 'לבטל את הקמת הארגון ולבטל את הזמנת הבעלים?',
  'organizationSetup.refresh': 'רענון מצב ההקמה',
  'organizationSetup.initialize': 'המשך ההקמה',
  'organizationSetup.initializing': 'בהכנה...',
  'organizationSetup.busy': 'ההקמה כבר מתבצעת. יש לרענן את המצב לפני ניסיון נוסף.',
  'organizationSetup.refreshBeforeRetry': 'יש לרענן את המצב השמור לפני ניסיון נוסף.',
  'organizationSetup.protocolsFailed': 'לא ניתן להתקין את הפרוטוקולים. השלבים שהושלמו נשמרו.',
  'organizationSetup.printFormsFailed': 'לא ניתן להתקין את טופסי ההדפסה. השלבים שהושלמו נשמרו.',
  'organizationSetup.langfuseFailed': 'לא ניתן לאמת את פרויקט ההנחיות והטלמטריה.',
  'organizationSetup.admissionFailed': 'לא ניתן להשלים את הגדרת הארגון. השלבים שהושלמו נשמרו.',
  'organizationSetup.awaitingLangfuse': 'ממתין לרישום פרויקט Langfuse על ידי סוכן ההקמה.',
  'organizationSetup.reloadFailed': 'לא ניתן לרענן את הבקשה. המצב המוצג לא השתנה.',
  'organizationSetup.protocols': 'פרוטוקולים',
  'organizationSetup.printForms': 'טופסי הדפסה',
  'organizationSetup.langfuse': 'הנחיות וטלמטריה',
  'organizationSetup.ownerAccepted': 'הבעלות התקבלה. הארגון נמצא בהכנה וטרם זמין לעבודה קלינית.',
  'organizationSetup.state.awaiting_owner': 'ממתין לבעלים',
  'organizationSetup.state.initializing': 'הארגון בהכנה',
  'organizationSetup.state.failed': 'ההקמה דורשת טיפול',
  'organizationSetup.state.ready': 'מוכן',
  'organizationSetup.state.cancelled': 'בוטל',
  'accountEmail.title': 'דוא״ל מאומת של החשבון',
  'accountEmail.address': 'כתובת דוא״ל',
  'accountEmail.add': 'הוספת דוא״ל',
  'accountEmail.request': 'שליחת קוד',
  'accountEmail.code': 'קוד אימות',
  'accountEmail.confirm': 'אימות דוא״ל',
  'accountEmail.resend': 'שליחת קוד חדש',
  'accountEmail.refresh': 'רענון מצב הדוא״ל',
  'accountEmail.submitted': 'הודעת האימות הוגשה לשליחה.',
  'accountEmail.uncertain': 'שליחת ההודעה לא אושרה. יש לבדוק את תיבת הדואר לפני בקשת קוד נוסף.',
  'accountEmail.verified': 'כתובת הדוא״ל אומתה.',
  'accountEmail.failed': 'אימות הדוא״ל אינו זמין. הערכים שהוזנו נשמרו.',
  'accountEmail.cooldown': 'יש להמתין דקה לפני בקשת קוד נוסף.',
  'accountEmail.missing': 'בקשת האימות הזו אינה עדכנית. יש לרענן את המצב או לבקש קוד חדש.',
  'accountEmail.expired': 'תוקף הקוד פג או שנגמרו ניסיונות האימות. יש לבקש קוד חדש.',
  'accountEmail.invalid': 'קוד האימות שגוי.',
  'accountEmail.occupied': 'כתובת הדוא״ל המאומתת שייכת לחשבון אחר. יש להיכנס לחשבון ההוא; חשבונות אינם ממוזגים.',
  'accountEmail.throttled': 'בקשות רבות מדי. יש לנסות שוב בעוד חמש עשרה דקות.',
  'accountEmail.unsaved': 'אימות הדוא״ל של החשבון',
  'users.invites.title': 'הזמנות',
  'users.invites.create': 'הזמנת משתמש',
  'users.invites.recipient': 'דוא״ל הנמען',
  'users.invites.channel': 'ערוץ ההזמנה',
  'users.invites.email': 'דוא״ל',
  'users.invites.telegram': 'Telegram',
  'users.invites.telegramRecipient': 'נמען ב־Telegram',
  'users.invites.web': 'קישור',
  'users.invites.webRecipient': 'נמען לפי קישור',
  'users.invites.kind.employee': 'עובד',
  'users.invites.kind.developer': 'מפתח',
  'users.invites.kind.first_owner': 'בעלים ראשון',
  'users.invites.kind.owner': 'בעלים',
  'users.invites.state.pending': 'ממתינה לקבלה',
  'users.invites.state.expired': 'פג תוקף',
  'users.invites.state.accepted': 'התקבלה',
  'users.invites.state.revoked': 'בוטלה',
  'users.invites.delivery': 'שליחה',
  'users.invites.delivery.pending': 'לא נשלחה',
  'users.invites.delivery.submitted': 'נמסרה לשרת הדואר',
  'users.invites.delivery.uncertain': 'תוצאת השליחה אינה ידועה',
  'users.invites.delivery.refused': 'השליחה נדחתה',
  'users.invites.personalLink': 'קישור אישי ל־Telegram',
  'users.invites.webLink': 'קישור ההזמנה',
  'users.invites.expires': 'בתוקף עד',
  'users.invites.empty': 'אין הזמנות',
  'users.invites.resend': 'הנפקת קישור חדש',
  'users.invites.revoke': 'ביטול הזמנה',
  'users.invites.resendConfirm': 'הקישור הקודם יפסיק לפעול. ההזמנה החדשה תהיה בתוקף לשלושה ימים.',
  'users.invites.revokeConfirm': 'לא יהיה אפשר להצטרף לארגון באמצעות הזמנה זו. חברויות קיימות לא ישתנו.',
  'users.invites.copy': 'העתקת קישור ההזמנה',
  'users.invites.copied': 'הקישור הועתק',
  'users.invites.copyNamed': 'העתקת קישור ההזמנה: {name}, בתוקף עד {expires}',
  'users.invites.copyFailed': 'לא ניתן להעתיק את הקישור.',
  'users.invites.linkUnavailable': 'לא ניתן להעתיק את הקישור. הנפיקו קישור חדש; הקישור הקודם יפסיק לפעול.',
  'users.invites.unsaved': 'פתוחה הזמנת משתמש שטרם נשלחה.',
  'users.invites.changed': 'ההזמנה השתנתה. יש לבדוק את מצבה לפני פעולה נוספת.',
  'users.invites.branchInvalid': 'הסניפים שנבחרו אינם זמינים עוד. יש לבדוק את סניפי הארגון.',
  'users.invites.notReady': 'הגדרת הארגון טרם הושלמה.',
  'users.invites.telegramUnavailable': 'בוט הצוות ב־Telegram אינו זמין. ייתכן שההזמנה נשמרה ללא קישור לשיתוף.',
  'users.invites.throttled': 'יותר מדי ניסיונות הזמנה. יש לנסות שוב מאוחר יותר.',
  'users.invites.invalid': 'יש לבדוק את הנמען, התפקידים ומקומות העבודה.',
  'users.invitation.title': 'הזמנת עובד',
  'users.invitation.missing': 'קישור ההזמנה אינו תקף. יש לבקש קישור חדש מהארגון.',
  'users.invitation.unavailable': 'שירות ההזמנות אינו זמין. ייתכן שהפעולה האחרונה לא הושלמה; יש לבדוק את ההזמנה לפני ניסיון נוסף.',
  'users.invitation.identityConflict': 'כתובת הדוא״ל או זהות Telegram זו משויכת לחשבון אחר. יש להיכנס לאותו חשבון.',
  'users.invitation.signInRequired': 'יש להיכנס לחשבון המשויך להזמנה זו.',
  'users.invitation.accountConflict': 'יש לצאת מהחשבון הנוכחי לפני יצירת חשבון אחר.',
  'users.invitation.accountKind': 'הזמנה זו אינה מתאימה לסוג החשבון הזה.',
  'users.invitation.alreadyMember': 'לחשבון הזה כבר יש גישה לארגון.',
  'users.invitation.restoreRequired': 'החברות מושבתת. יש לאשר את שחזורה כדי לקבל את ההזמנה החדשה.',
  'users.invitation.closed': 'ההזמנה הזו אינה פתוחה עוד.',
  'users.invitation.changed': 'ההזמנה או הגדרות הארגון השתנו. יש לבקש הזמנה חדשה.',
  'users.invitation.credentialsRequired': 'יש לבחור שם משתמש וסיסמה שעומדת בדרישות.',
  'users.invitation.invalidInput': 'יש לבדוק את פרטי החשבון והשם שהוזנו.',
  'users.invitation.telegramRequired': 'יש לפתוח שוב את ההזמנה מבוט הצוות ב־Telegram כדי לאמת את הזהות.',
  'users.invitation.telegramUnknown': 'לזהות Telegram זו אין עדיין חשבון. יש ליצור חשבון או להיכנס לחשבון הקיים.',
  'users.invitation.passwordRequired': 'יש להחליף את הסיסמה הזמנית לפני קבלת ההזמנה.',
  'users.invitation.forbidden': 'אי אפשר לקבל את ההזמנה עם החשבון או הגישה הנוכחיים ליישום.',
  'users.invitation.expired': 'תוקף ההזמנה פג. יש לבקש קישור חדש מהארגון.',
  'users.invitation.revoked': 'הארגון ביטל את ההזמנה הזו.',
  'users.invitation.joined': 'החברות אושרה.',
  'users.invitation.disabled': 'ההזמנה כבר התקבלה, אך החברות מושבתת כעת.',
  'users.invitation.accepted': 'ההזמנה כבר התקבלה. יש להיכנס לחשבון שקיבל אותה כדי לבדוק את הגישה הנוכחית.',
  'users.invitation.account': 'חשבון',
  'users.invitation.welcome': 'ברוכים הבאים. צרו חשבון כדי להצטרף; את כל השאר תגדירו אחר כך בכרטיס שלכם.',
  'users.invitation.useExisting': 'יש לי כבר חשבון',
  'users.invitation.createInstead': 'יצירת חשבון חדש',
  'users.invitation.addPassword': 'הוספת שם משתמש וסיסמה',
  'users.invitation.restore': 'שחזור החברות שלי בארגון הזה',
  'users.invitation.working': 'נא להמתין...',
  'users.invitation.accept': 'אישור הצטרפות',
  'users.invitation.create': 'יצירת חשבון והצטרפות',
  'users.invitation.telegramSignIn': 'כניסה באמצעות Telegram',
  'users.invitation.recheck': 'בדיקת מצב ההזמנה',
  'users.invitation.unsaved': 'בטופס ההזמנה יש פרטי חשבון או פרופיל שלא נשמרו.',
  "users.title": "עובדים",
  'users.subtitle': 'עובדי הארגון, תפקידים וחברות.',
  'users.loading': 'טוען משתמשים',
  'users.loadFailed': 'לא ניתן לטעון משתמשים',
  'users.forbidden': 'אין לך הרשאה לקרוא משתמשים במרפאה הזו',
  'users.tableLabel': 'משתמשי המרפאה',
  'users.empty': 'אין משתמשים גלויים',
  'users.emptyDetail': 'חשבונות משתמש שנוצרים למרפאה הזו יופיעו כאן.',
  'users.noMatches': 'אין משתמשים שמתאימים למסננים',
  'users.noMatchesDetail': 'הרחב או נקה את מסנני העמודות כדי לחזור לרשימת המשתמשים המלאה.',
  'users.column.name': 'שם',
  'users.column.username': 'שם משתמש',
  'users.column.roles': 'תפקידים',
  'users.column.status': 'מצב',
  'users.column.kind': 'סוג',
  'users.column.contact': 'קשר',
  'users.status.active': 'פעיל',
  'users.status.disabled': 'חסום',
  'users.badge.owner': 'בעלים',
  'users.badge.you': 'את/ה',
  'users.badge.admin': 'מנהל',
  'users.actions': 'פעולות',
  'users.editNamed': 'עריכת {name}',
  'users.sections': 'חלקי המשתמש',
  'users.createTitle': 'הוספת משתמש',
  'users.username': 'שם משתמש',
  'users.initialPassword': 'סיסמה ראשונית',
  'users.initialPasswordHint': 'ניתן לדרוש מהחשבון החדש לשנות אותה אחרי הכניסה.',
  'users.lifecycleStatus': 'מצב כניסה',
  'users.passwordChangeRequired': 'דרוש שינוי סיסמה בכניסה הבאה',
  'users.roles': 'תפקידים',
  'users.transferOwnership': 'העברת בעלות',
  'users.transferTitle': 'העברת בעלות על המרפאה',
  'users.transferDescription': 'הפיכת {name} לבעלי המרפאה. התפקידים הרגילים שלך לא ישתנו.',
  'users.transferCancel': 'ביטול',
  'users.membershipStatus': 'גישה לארגון',
  'users.remove': 'מחיקה',
  'users.removeNamed': 'מחיקת {name}',
  'users.removeConfirm': 'העובד ייעלם מרשימת המשתמשים של הארגון הזה. ניתן למחוק רק עובד שאין לו כאן רשומות או אובייקטים רפואיים; אחרת ניתן רק לחסום אותו. האובייקטים שיצר והגישה לארגונים אחרים יישמרו. אם לא יישאר ארגון אחר, החשבון הכללי ייחסם.',
  'users.removeFailed': 'לא ניתן למחוק את המשתמש.',
  'users.removeMedical': 'לא ניתן למחוק: לעובד יש רשומות או אובייקטים רפואיים בארגון הזה. יש לחסום אותו במקום למחוק.',
  'users.removed': 'המשתמש הוסר מהארגון הזה. החשבון שלו בארגונים אחרים לא השתנה.',
  'users.removedAccountBlocked': 'המשתמש הוסר מהארגון הזה. לא נותר ארגון אחר, ולכן החשבון הכללי נחסם.',
  'users.membershipDisable': 'חסימה',
  'users.membershipRestore': 'ביטול חסימה',
  'users.membershipDisableNamed': 'חסימת {name}',
  'users.membershipRestoreNamed': 'ביטול החסימה של {name}',
  'users.membershipDisableConfirm': 'העובד לא יוכל לעבוד בארגון הזה עד לביטול החסימה. התפקידים, ההיסטוריה והגישה לארגונים אחרים לא ישתנו.',
  'users.membershipRestoreConfirm': 'העובד יקבל שוב גישה לארגון הזה עם התפקידים הקיימים.',
  'users.membershipUnsaved': 'יש לשמור או לבטל שינויים לפני שינוי הגישה.',
  'users.accessHint': 'עובד חסום לא יכול לעבוד כאן עד לביטול החסימה. ניתן למחוק רק עובד שאין לו רשומות רפואיות בארגון הזה.',
  'users.transferConfirm': 'העברת בעלות',
  'users.transferring': 'מעביר בעלות',
  'users.transferFailed': 'לא ניתן להעביר את הבעלות על המרפאה.',
  'users.practitioner': 'פרופיל מטפל',
  'users.practitionerHint': 'יש להשתמש בזה רק לקלינאים שצריכים הקשר מטפל בתהליכים קליניים.',
  'users.profession': 'מקצוע משרד הבריאות',
  'users.expertise': 'מומחיות משרד הבריאות',
  'users.codingNone': 'לא צוין',
  'users.create': 'יצירת משתמש',
  'users.creating': 'יוצר',
  'users.created': 'המשתמש נוצר',
  'users.saving': 'שומר',
  'users.changedConcurrently': 'מישהו אחר שינה את המשתמש בזמן שהטופס היה פתוח.',
  'users.usernameTaken': 'שם המשתמש הזה כבר בשימוש.',
  'users.createFailed': 'לא ניתן ליצור את המשתמש.',
  'users.unsavedIntent': 'טופס משתמש \u2014 נתונים שלא נשמרו',
  'users.saveForbidden': 'אין לך הרשאה לשנות משתמשים.',
  'users.saveFailed': 'לא ניתן לשמור את המשתמש.',
  'users.detailsReadOnly': 'לא ניתן לשנות כאן את פרטי הקשר של העובד הזה.',
  'users.rolesOwnerNote': 'שינוי תפקידים רגילים אינו מעביר בעלות ואינו מסיר את סמכות הבעלים.',
  'users.rolesNone': 'אין תפקידים לשיוך',
  'users.saveRoles': 'שמירת תפקידים',
  'users.savingRoles': 'שומר תפקידים',
  'users.rolesSaved': 'התפקידים נשמרו',
  'users.rolesChangedConcurrently': 'מישהו אחר שינה את המשתמש בזמן שהתפקידים היו פתוחים. יש לטעון מחדש כדי לראות את התפקידים הנוכחיים; הבחירה שלך נשארת.',
  'users.rolesForbidden': 'אין לך הרשאה לשנות את התפקידים של המשתמש הזה.',
  'users.rolesNotFound': 'המשתמש הזה כבר אינו קיים.',
  'users.rolesUnavailable': 'השרת אינו זמין. הבחירה שלך נשארת.',
  'users.rolesSaveFailed': 'לא ניתן לשמור את התפקידים.',
  'users.reloadUser': 'טעינה מחדש של המשתמש',
  'users.userReloaded': 'נטען מחדש מהשרת. יש לבדוק את התפקידים ולשמור שוב.',
  'users.reloadFailed': 'לא ניתן לטעון מחדש את המשתמש.',
  'users.backToUsers': 'חזרה למשתמשים',
  'users.openCard': 'פתיחת כרטיס העובד',
  'users.changeRoles': 'שינוי תפקידים',
  'users.branchesNone': 'לא שויכו סניפים.',
  'users.branchesFromWorkplaces': 'הסניפים של איש צוות רפואי נקבעים לפי מקומות העבודה שלו.',
  'users.ownership': 'בעלות',
  'users.ownershipHint': 'לבעלים יש סמכות מלאה על הארגון, ובכל רגע יש בעלים אחד בלבד.',
  'users.card.title': 'כרטיס עובד',
  'users.card.notFound': 'העובד הזה אינו מופיע במדריך הארגון.',
  'users.card.access': 'גישה',
  'users.card.manageAccess': 'שינוי גישה',

  "roles.title": "תפקידים והרשאות",
  'roles.subtitle': 'חבילות תפקידים כלל-מערכתיות, ההרשאות שהן מעניקות והיכן הן משויכות.',
  'roles.loading': 'טוען תפקידים',
  'roles.loadFailed': 'לא ניתן לטעון תפקידים',
  'roles.forbidden': 'אין לך הרשאה לקרוא הגדרות תפקידים',
  'roles.unavailable': 'הגדרות התפקידים אינן זמינות כרגע',
  'roles.capabilitiesLoading': 'טוען את קטלוג ההרשאות',
  'roles.capabilitiesFailed': 'לא ניתן לטעון את קטלוג ההרשאות',
  'roles.tableLabel': 'הגדרות תפקידים',
  'roles.empty': 'אין תפקידים',
  'roles.emptyDetail': 'הגדרות תפקידים שהשרת מחזיר יופיעו כאן.',
  'roles.noMatches': 'אין תפקידים שמתאימים למסננים',
  'roles.noMatchesDetail': 'הרחב או נקה את מסנני העמודות כדי לחזור לרשימת התפקידים המלאה.',
  'roles.column.name': 'שם',
  'roles.column.code': 'קוד',
  'roles.column.kind': 'סוג',
  'roles.column.lifecycle': 'מצב',
  'roles.column.grants': 'הרשאות',
  'roles.column.assignments': 'שיוכים',
  'roles.column.areas': 'תחומים',
  'roles.column.updated': 'שינוי אחרון',
  'roles.kind.editable': 'תפקיד ניתן לעריכה',
  'roles.kind.sealed': 'תפקיד חתום',
  'roles.kind.ownerAuthority': 'סמכות בעלים',
  'roles.lifecycle.active': 'פעיל',
  'roles.lifecycle.retired': 'הוצא משימוש',
  'roles.origin.builtIn': 'מובנה',
  'roles.origin.custom': 'מותאם אישית',
  'roles.origin.relation': 'נגזר מיחס',
  'roles.unrecognized': '{value} (לא מזוהה)',
  'roles.actions': 'פעולות',
  'roles.editNamed': 'עריכת {name}',
  'roles.openNamed': 'פתיחת {name}',
  'roles.createTitle': 'תפקיד חדש',
  'roles.cloneTitle': 'שכפול תפקיד',
  'roles.clonedFrom': 'ההרשאות והתיאור הועתקו מ-{name}. שיוכים, מקור והיסטוריה אינם מועתקים.',
  'roles.code': 'קוד תפקיד',
  'roles.codeHint': 'אותיות לטיניות קטנות, ספרות וקו תחתון. לא ניתן לשנות את הקוד אחרי היצירה.',
  'roles.displayName': 'שם',
  'roles.description': 'תיאור',
  'roles.reason': 'סיבת השינוי',
  'roles.reasonHint': 'נשמר ביומן הביקורת יחד עם שינויי ההרשאות המדויקים.',
  'roles.capabilities': 'הרשאות',
  'roles.capabilitySearch': 'חיפוש הרשאות',
  'roles.capabilityNoMatches': 'אין הרשאות מתאימות',
  'roles.keptGrants': 'הרשאות שהקטלוג הזה אינו יכול להציע, נשמרות כפי שהן',
  'roles.audience.product': 'מוצר',
  'roles.audience.developer': 'מפתחים',
  'roles.area.ai': 'בינה מלאכותית',
  'roles.area.app': 'יישום',
  'roles.area.authorization': 'הרשאות',
  'roles.area.clinic': 'ניהול המרפאה',
  'roles.area.clinical': 'עבודה קלינית',
  'roles.area.documents': 'מסמכים',
  'roles.area.finance': 'כספים',
  'roles.area.imagingStudies': 'בדיקות דימות',
  'roles.area.laboratory': 'מעבדה',
  'roles.area.messages': 'הודעות',
  'roles.area.notifications': 'התראות',
  'roles.area.patientCases': 'תיקי מטופלים',
  'roles.area.patientHospitalStays': 'אשפוזים',
  'roles.area.patientLeads': 'פניות מטופלים',
  'roles.area.patientMessages': 'הודעות מטופלים',
  'roles.area.patients': 'מטופלים',
  'roles.area.practitioners': 'מטפלים',
  'roles.area.printForms': 'טפסי הדפסה',
  'roles.area.protocols': 'פרוטוקולים',
  'roles.area.referenceData': 'נתוני ייחוס',
  'roles.area.schedule': 'יומן',
  'roles.area.tasks': 'משימות',
  'roles.area.tracker': 'מעקב',
  'roles.scopeFor': 'היקף עבור {name}',
  'roles.scope.none': 'לא מוענק',
  'roles.scope.own': 'עצמי',
  'roles.scope.related': 'קשור',
  'roles.scope.clinic': 'מרפאה',
  'roles.scope.platform': 'פלטפורמה',
  'roles.grants': 'הרשאות בתוקף',
  'roles.grantsNone': 'רשומה זו אינה מעניקה דבר.',
  'roles.facts': 'הגדרה',
  'roles.fact.origin': 'מקור',
  'roles.fact.lifecycle': 'מצב',
  'roles.fact.assignments': 'חשבונות משויכים',
  'roles.fact.updated': 'שינוי אחרון',
  'roles.changes': 'שינויים',
  'roles.noChanges': 'עדיין אין שינויים.',
  'roles.change.added': 'נוסף',
  'roles.change.removed': 'הוסר',
  'roles.change.scopeChanged': 'ההיקף השתנה',
  'roles.change.name': 'שם',
  'roles.change.description': 'תיאור',
  'roles.change.fromTo': '{from} ← {to}',
  'roles.change.empty': 'לא הוגדר',
  'roles.save': 'שמירת תפקיד',
  'roles.saving': 'שומר',
  'roles.saved': 'התפקיד נשמר',
  'roles.create': 'יצירת תפקיד',
  'roles.creating': 'יוצר',
  'roles.created': 'התפקיד נוצר',
  'roles.clone': 'שכפול',
  'roles.retire': 'הוצאת התפקיד משימוש',
  'roles.retiring': 'מוציא משימוש',
  'roles.retired': 'התפקיד הוצא משימוש',
  'roles.restore': 'שחזור התפקיד',
  'roles.restoring': 'משחזר',
  'roles.restored': 'התפקיד שוחזר',
  'roles.impactTitle': 'להחיל את השינוי על כל המרפאות?',
  'roles.impactDescription': 'ההרשאות שלהם ישתנו מיד עם שמירת התפקיד. חשבונות המחזיקים בתפקיד הזה בכל המרפאות: {count}.',
  'roles.impactConfirm': 'שמירה והחלה',
  'roles.cancel': 'ביטול',
  'roles.retireTitle': 'להוציא את התפקיד משימוש?',
  'roles.retireDescription': 'תפקיד שהוצא משימוש לא ניתן לשיוך. ההגדרה וההיסטוריה נשמרות, וניתן לשחזר אותו מאוחר יותר.',
  'roles.retireConfirm': 'הוצאה משימוש',
  'roles.restoreTitle': 'לשחזר את התפקיד?',
  'roles.restoreDescription': 'התפקיד יחזור להיות ניתן לשיוך תחת גרסה חדשה.',
  'roles.restoreConfirm': 'שחזור',
  'roles.readOnly': 'לקריאה בלבד',
  'roles.sealedDefault': 'התפקיד הזה חתום על ידי גרסת הפלטפורמה ולא ניתן לערוך אותו כאן.',
  'roles.ownerAuthorityNote': 'סמכות הבעלים נגזרת מיחס הבעלות על המרפאה. זה אינו תפקיד: לא ניתן לשייך אותו ולא ניתן לערוך אותו כאן.',
  'roles.retiredNote': 'התפקיד הזה הוצא משימוש ולא ניתן לשיוך. יש לשחזר אותו לפני עריכה.',
  'roles.validation.code': 'יש להזין קוד תפקיד',
  'roles.validation.name': 'יש להזין שם',
  'roles.validation.grants': 'יש להעניק לפחות הרשאה אחת',
  'roles.validation.reason': 'יש להזין סיבה',
  'roles.error.notFound': 'התפקיד הזה כבר אינו קיים בשרת.',
  'roles.error.forbidden': 'אין לך הרשאה לשנות את התפקיד הזה.',
  'roles.error.scopeInvalid': 'השרת דחה אחד מהיקפי ההרשאה. יש לבדוק את ההיקפים ולשמור שוב.',
  'roles.error.changedConcurrently': 'מישהו אחר שינה את התפקיד בזמן שהעורך היה פתוח. יש לטעון אותו מחדש כדי לראות את הגרסה הנוכחית; העריכות שטרם נשמרו נשארות.',
  'roles.error.inUse': 'התפקיד הזה עדיין משויך ולא ניתן להוציאו משימוש.',
  'roles.error.unavailable': 'השרת אינו זמין. העריכות שטרם נשמרו נשארות.',
  'roles.error.invalid': 'השרת דחה את הגדרת התפקיד הזו.',
  'roles.error.unknown': 'השרת החזיר תשובה לא צפויה. העריכות שטרם נשמרו נשארות.',
  'roles.reload': 'טעינה מחדש של התפקיד',
  'roles.reloaded': 'נטען מחדש מהשרת. יש לבדוק את השינויים ולשמור שוב.',
  'roles.reloadFailed': 'לא ניתן לטעון מחדש את התפקיד.',
  'roles.unsavedIntent': 'הגדרת תפקיד \u2014 נתונים שלא נשמרו',
  'roles.detailLoading': 'טוען תפקיד',
  'roles.detailFailed': 'לא ניתן לטעון את התפקיד הזה',
}
